
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, User, Search, Filter } from "lucide-react";
import { BookingManagement } from "../booking/BookingManagement";

interface BookingDetails {
  id: string;
  provider: {
    name: string;
    specialty: string;
  };
  date: string;
  time: string;
  patient: {
    name: string;
    email: string;
    phone: string;
  };
  reason: string;
  status: "confirmed" | "cancelled" | "rescheduled" | "completed";
}

const mockBookings: BookingDetails[] = [
  {
    id: "BK-2024-001",
    provider: { name: "Dr. Sarah Smith", specialty: "Cardiology" },
    date: "February 15, 2024",
    time: "10:00 AM",
    patient: { name: "John Doe", email: "john@example.com", phone: "(555) 123-4567" },
    reason: "Annual Checkup",
    status: "confirmed"
  },
  {
    id: "BK-2024-002",
    provider: { name: "Dr. Michael Wilson", specialty: "Dermatology" },
    date: "January 20, 2024",
    time: "2:30 PM",
    patient: { name: "John Doe", email: "john@example.com", phone: "(555) 123-4567" },
    reason: "Skin Consultation",
    status: "completed"
  },
  {
    id: "BK-2024-003",
    provider: { name: "Dr. Emily Johnson", specialty: "Pediatrics" },
    date: "January 10, 2024",
    time: "11:15 AM",
    patient: { name: "John Doe", email: "john@example.com", phone: "(555) 123-4567" },
    reason: "Follow-up Visit",
    status: "cancelled"
  }
];

export function BookingHistory() {
  const [bookings, setBookings] = useState<BookingDetails[]>(mockBookings);
  const [selectedBooking, setSelectedBooking] = useState<BookingDetails | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "rescheduled":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch = 
      booking.provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || booking.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleBookingUpdated = (updatedBooking: BookingDetails) => {
    setBookings(prev => 
      prev.map(booking => 
        booking.id === updatedBooking.id ? updatedBooking : booking
      )
    );
    setSelectedBooking(updatedBooking);
  };

  if (selectedBooking) {
    return (
      <BookingManagement
        booking={selectedBooking}
        onBack={() => setSelectedBooking(null)}
        onBookingUpdated={handleBookingUpdated}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Booking History</h1>
        <p className="text-gray-600">View and manage your appointments</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by provider, reason, or booking ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
              <p className="text-gray-600">
                {searchTerm || statusFilter !== "all" 
                  ? "Try adjusting your search or filter criteria"
                  : "You haven't made any appointments yet"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredBookings.map((booking) => (
            <Card key={booking.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <div>
                          <div className="font-medium">{booking.provider.name}</div>
                          <div className="text-sm text-blue-600">{booking.provider.specialty}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <div>
                          <div className="font-medium">{booking.date}</div>
                          <div className="text-sm text-gray-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {booking.time}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <strong>Reason:</strong> {booking.reason}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        ID: {booking.id}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge className={getStatusColor(booking.status)}>
                      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </Badge>
                    <Button 
                      onClick={() => setSelectedBooking(booking)}
                      variant="outline"
                      size="sm"
                    >
                      Manage
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
